/**
 * The `/auth/*` pages own their own auth state: sign-in drives the Keycloak
 * redirect itself, sign-out is tearing the session down, and the error screens
 * are terminal — they exist precisely because sign-in cannot succeed. On these
 * pages "no session" is the expected state, so neither an automatic sign-in nor
 * an automatic sign-out may fire.
 *
 * Either one loops. A Keycloak identity that cannot be provisioned (no
 * `tenantId` claim and no invitation) lands on
 * `/auth/error/signin?error=unable_to_create_user`; restarting sign-in — or
 * signing out and thereby routing back through sign-in — bounces off Keycloak's
 * still-valid SSO cookie into the same failure, so the user never gets to read
 * the error.
 *
 * The same rule gates the server side: `proxy.ts` serves these paths without a
 * session check, so a user holding no session can still reach `/auth/signOut`
 * to clear Keycloak's SSO cookie — the only way out of that failure.
 *
 * This bounds only the loops that pass through `/auth/*`. One that spins on an
 * ordinary page (enrichment failing for a provisioned user) is bounded by
 * `signInLoopGuard` instead.
 */
const AUTH_PATH_PREFIX = '/auth/';

/** True for any `/auth/*` page (sign-in, sign-out, error screens). */
export const isAuthPage = (pathname: string): boolean =>
  pathname.startsWith(AUTH_PATH_PREFIX);
